import { For, Show, createEffect, onCleanup } from 'solid-js';

import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Text } from '@/components/ui/text';
import { DataGuard } from '@components/DataGuard';
import LayoutHeader from '@components/LayoutHeader';
import { PageSearch, useSearchParamSync } from '@components/PageSearch';
import { pill } from '@features/communityContent/topics';
import { useSearchParams } from '@solidjs/router';
import { useEvents } from '@state/events';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ArrowLeft, ArrowRight } from 'lucide-solid';
import { useService } from 'solid-services';
import { css } from 'styled-system/css';
import { Flex, Grid } from 'styled-system/jsx';
import { useCalendarState } from '../../../state/events/calendar-state';
import SideBar from './Sidebar';

import Disclaimer from '@components/Disclaimer';
import Panel from '@components/Panel';
import { when } from '@core';
import Month from './Month';
import Week from './Week';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

export default function CalendarPage() {
  const userTz = dayjs.tz.guess();
  const { events } = useService(useEvents)();
  const {
    viewDate, nextMonth, prevMonth, today,
    search, setSearch, groupFilter, setGroupFilter, eventGroups, focusEvent, clearFilters,
  } = useService(useCalendarState)();
  const [params, setParams] = useSearchParams();

  const isMobile = when.mdDown;

  useSearchParamSync(search, setSearch);

  // Global search links to /events?event=<id>: jump to that event once events have loaded
  createEffect(() => {
    const id = params.event;
    if (typeof id !== 'string' || !events.data) return;
    focusEvent(id);
    setParams({ event: undefined }, { replace: true });
  });

  onCleanup(() => {
    today();
    clearFilters();
  });

  return (
    <>
      <Grid gridTemplateRows='auto auto 1fr auto' height='100%' gap='0'>
        <LayoutHeader
          title={viewDate().format('dddd, MMMM D, YYYY')}
          subTitle={userTz.replace('/', ', ').replaceAll('_', ' ')}
          rightContent={
            <Flex gap='2'>
              <IconButton size='xs' variant="plain" onClick={prevMonth}>
                <ArrowLeft />
              </IconButton>
              <Button size='xs' colorPalette='jade' variant='subtle' onClick={today}>Today</Button>
              <IconButton size='xs' variant="plain" onClick={nextMonth}>
                <ArrowRight />
              </IconButton>
            </Flex>
          }
        />

        <Flex
          gap='10px'
          alignItems='center'
          px={{ base: '6', mdDown: '3' }}
          pb='3'
          flexWrap={{ base: 'wrap', mdDown: 'nowrap' }}
          flexDirection={{ base: 'row', smDown: 'column' }}
        >
          <PageSearch
            value={search()}
            onInput={setSearch}
            placeholder='Search events…'
            class={css({ w: { base: '220px', smDown: 'full' }, flexShrink: 0 })}
          />
          <Flex
            role='group'
            aria-label='Filter by group'
            gap='7px'
            w={{ smDown: 'full' }}
            overflowX='auto'
            class={css({ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } })}
          >
            <button type='button' class={pill(groupFilter() === null)} onClick={() => setGroupFilter(null)}>
              All groups
            </button>
            <For each={eventGroups()}>
              {(group) => (
                <button
                  type='button'
                  class={pill(groupFilter() === group)}
                  onClick={() => setGroupFilter(groupFilter() === group ? null : group)}
                >
                  {group}
                </button>
              )}
            </For>
          </Flex>
          <Show when={search().trim() || groupFilter()}>
            <Text fontSize='xs' color='fg.subtle' whiteSpace='nowrap'>
              Showing matching events only ·{' '}
              <button type='button' class={css({ cursor: 'pointer', textDecoration: 'underline' })} onClick={clearFilters}>
                clear
              </button>
            </Text>
          </Show>
        </Flex>

        <DataGuard query={events}>
          {() => (
            <Panel>
              <Show when={isMobile()} fallback={<Month />}>
                <Week />
              </Show>
            </Panel>
          )}
        </DataGuard>
        <Disclaimer />
      </Grid>
      <SideBar />
    </>
  );
}
